import React, { useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import { Input, Modal } from 'antd';

import IntlMessages from '@src/util/IntlMessages';

const INITIAL_CONFIRM_DATA = {
  message: null,
  onConfirm: null,
  title: null,
  okDanger: false,
  okText: null,
  content: null,
  okDisabled: false,
};

const ConfirmDeleteDialog = ({ confirmWord, confirmData, isVisible, handleConfirm, handleCancel }) => {
  const intl = useIntl();
  const [typedValue, setTypedValue] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);
  const pendingRef = useRef(false);

  const translationKeys = {
    DELETE: 'general.confirm.delete',
    CANCEL: 'general.confirm.cancel',
  };

  const translationKey = translationKeys[confirmWord] || null;

  const CONFIRM_WORD = translationKey
    ? intl.formatMessage({ id: translationKey }).toUpperCase()
    : confirmWord.toUpperCase();

  const canConfirm = typedValue === CONFIRM_WORD;

  const handleModalCancel = () => {
    if (pendingRef.current) return;
    handleCancel();
    setTypedValue('');
  };

  const handleModalOk = async () => {
    if (pendingRef.current || !canConfirm || confirmData.okDisabled) return;
    pendingRef.current = true;
    setIsConfirming(true);
    try {
      const confirmed = await handleConfirm();
      if (confirmed !== false) setTypedValue('');
    } finally {
      pendingRef.current = false;
      setIsConfirming(false);
    }
  };

  return (
    <Modal
      destroyOnClose
      centered
      closable={!isConfirming}
      keyboard={!isConfirming}
      confirmLoading={isConfirming}
      cancelButtonProps={{ disabled: isConfirming }}
      maskClosable={false}
      title={confirmData.title || <IntlMessages id="general.confirmMessage" />}
      open={isVisible}
      onOk={handleModalOk}
      okText={confirmData.okText || <IntlMessages id="button.ok" />}
      cancelText={<IntlMessages id="button.cancel" />}
      onCancel={handleModalCancel}
      okButtonProps={{
        disabled: isConfirming || confirmData.okDisabled || !canConfirm,
        danger: confirmData.okDanger,
      }}
    >
      {confirmData.message && (
        <p style={{ whiteSpace: 'pre-line', marginBottom: 16 }}>{confirmData.message}</p>
      )}
      {confirmData.content}
      {!confirmData.okDisabled && (
        <Input
          autoFocus
          disabled={isConfirming}
          value={typedValue}
          onChange={event => setTypedValue(event.target.value.toUpperCase())}
          placeholder={intl.formatMessage(
            { id: 'general.confirm.delete.placeholder' },
            { word: CONFIRM_WORD },
          )}
          onPressEnter={() => {
            if (canConfirm) {
              handleModalOk();
            }
          }}
        />
      )}
    </Modal>
  );
};

const useConfirmDelete = (options = {}) => {
  const {
    confirmWord = 'DELETE', // 'DELETE' (default) or 'CANCEL' or custom
  } = options;

  const [isVisible, setIsVisible] = useState(false);
  const [confirmData, setConfirmData] = useState(INITIAL_CONFIRM_DATA);

  const showConfirmDelete = (message, onConfirm, extra = {}) => {
    setConfirmData({
      message,
      onConfirm,
      title: extra.title ?? null,
      okDanger: Boolean(extra.okDanger),
      okText: extra.okText ?? null,
      content: extra.content ?? null,
      okDisabled: Boolean(extra.okDisabled),
    });
    setIsVisible(true);
  };

  const handleConfirm = async () => {
    if (confirmData.onConfirm) {
      const confirmed = await confirmData.onConfirm();
      if (confirmed === false) return false;
    }
    setIsVisible(false);
    setConfirmData(INITIAL_CONFIRM_DATA);
  };

  const handleCancel = () => {
    setIsVisible(false);
    setConfirmData(INITIAL_CONFIRM_DATA);
  };

  const confirmDeleteModal = (
    <ConfirmDeleteDialog
      confirmWord={confirmWord}
      confirmData={confirmData}
      isVisible={isVisible}
      handleConfirm={handleConfirm}
      handleCancel={handleCancel}
    />
  );

  // Preserve the legacy component API; new callers should render the element directly.
  const ConfirmDeleteModal = () => confirmDeleteModal;

  return {
    showConfirmDelete,
    ConfirmDeleteModal,
    confirmDeleteModal,
  };
};

export default useConfirmDelete;
